import type { BoxPrices } from '@library/api-pricing'
import type { PriceBreakdownCardProps, PriceRow } from '@library/landing-pages'

import type { NumPersonsToBoxDescriptorsList } from '../../boxPricesModels'
import { CONTROL_DISCOUNT_STAGES, TAPERED_DISCOUNT_STAGES } from '../discount/config'
import { getPromotionalPricing } from '../discount/discounts'

/**
 * Hardcoded because `fetchPrices` always reports `delivery_total: "0.00"` — a placeholder, not a
 * real price. Replace this with the API value once it returns one.
 */
export const STANDARD_DELIVERY_PRICE = 3.99

const LOWEST_RECIPE_COUNT = 2

const formatGBP = (amount: number) => `£${amount.toFixed(2)}`

/**
 * The route stores the recipe selection as an index; the calculator speaks counts. These convert
 * at that boundary, leaving the index representation to the control arm that already uses it.
 */
export const recipeIndexToCount = (index: number) => index + LOWEST_RECIPE_COUNT

export const recipeCountToIndex = (count: number) => count - LOWEST_RECIPE_COUNT

/**
 * `num_portions` is the *recipe* count despite its name — `useMarketingPricingConfig` sets it
 * from the pricing data's own recipe key. Read the value, never the array position: a `{2, 4}`
 * range would otherwise become `[2, 3]`, offering an unpriced option and hiding a priced one.
 */
export const getRecipeOptionsFromBoxInfo =
  (boxInfo: { [people: number]: NumPersonsToBoxDescriptorsList }) =>
  (people: number): number[] =>
    (boxInfo[people] ?? []).map(({ num_portions: recipeCount }) => recipeCount)

/**
 * Deliberately not the shared `config/portionSizes.ts`, whose `suitableFor` list feeds several
 * other surfaces that would change copy if it were reshaped into one sentence.
 *
 * 1 is absent on purpose. The comma before "or" appears for 2/3 but not 4/5 — signed-off copy,
 * not a typo to normalise away.
 */
const SUITABILITY_BY_PEOPLE: Record<number, string> = {
  2: 'Great for 2 adults, or 1 adult and up to 2 kids',
  3: 'Great for 3 adults, or 2 adults and up to 2 kids',
  4: 'Great for 4 adults or 2 adults and 2-3 kids',
  5: 'Great for 5 adults or 2-3 adults and 2-4 kids',
}

export const getSuitability = (people: number): string => SUITABILITY_BY_PEOPLE[people] ?? ''

export const getPriceBreakdown =
  (pricingData: BoxPrices, isTaperedDiscountCurveEnabled: boolean) =>
  (people: number, recipes: number): PriceBreakdownCardProps & { pricePerPortion: PriceRow } => {
    const { gourmet } = pricingData[people][recipes]
    const discountStages = isTaperedDiscountCurveEnabled
      ? TAPERED_DISCOUNT_STAGES
      : CONTROL_DISCOUNT_STAGES

    const undiscountedRecipeTotal = Number(gourmet.recipe_total)
    const { firstBoxPriceWithDiscount, firstBoxPerPortionPriceWithDiscount } =
      getPromotionalPricing(recipes, people, undiscountedRecipeTotal, discountStages)
    const discountedRecipeTotal = Number(firstBoxPriceWithDiscount)

    const isDeliveryFree = gourmet.is_delivery_free
    const deliveryPayable = isDeliveryFree ? 0 : STANDARD_DELIVERY_PRICE

    return {
      boxLabel: 'Your 1st box',
      discountBadgeLabel: `${discountStages[0].amount}% off`,
      pricePerPortion: {
        originalPrice: formatGBP(Number(gourmet.price_per_portion)),
        currentPrice: formatGBP(Number(firstBoxPerPortionPriceWithDiscount)),
      },
      recipesLabel: `${recipes} recipes for ${people} ${people === 1 ? 'person' : 'people'}`,
      recipesPrice: {
        originalPrice: formatGBP(undiscountedRecipeTotal),
        currentPrice: formatGBP(discountedRecipeTotal),
      },
      deliveryPrice: {
        originalPrice: formatGBP(STANDARD_DELIVERY_PRICE),
        currentPrice: isDeliveryFree ? 'FREE' : formatGBP(STANDARD_DELIVERY_PRICE),
        currentPriceIsFree: isDeliveryFree,
      },
      totalPrice: {
        // Sums the lines above it, delivery included. This deliberately differs from the Figma
        // mock, which shows the recipes figure alone — see OQ-1 in the proposal.
        originalPrice: formatGBP(undiscountedRecipeTotal + STANDARD_DELIVERY_PRICE),
        currentPrice: formatGBP(discountedRecipeTotal + deliveryPayable),
      },
    }
  }
